import { Link } from '@tanstack/react-router'
import {
  ChevronDown,
  ChevronRight,
  File,
  FileText,
  FolderOpen,
  MoreHorizontal,
  Plus,
  Trash2,
  Move,
  Copy,
} from 'lucide-react'
import { useState, useCallback } from 'react'

import { Button } from '@/components/ui/button'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { useDocuments, type Document } from '@/hooks'
import { cn } from '@/lib/utils'

interface DocumentTreeProps {
  knowledgeBaseId: string
  selectedDocumentId?: string
  onSelectDocument?: (documentId: string) => void
  onCreateDocument?: (parentId?: string) => void
  onDeleteDocument?: (document: Document) => void
  onMoveDocument?: (document: Document) => void
}

/**
 * 文档树组件
 */
export function DocumentTree({
  knowledgeBaseId,
  selectedDocumentId,
  onSelectDocument,
  onCreateDocument,
  onDeleteDocument,
  onMoveDocument,
}: DocumentTreeProps) {
  const { data: result, isLoading } = useDocuments(knowledgeBaseId, { parentId: null })
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  if (isLoading) {
    return <DocumentTreeSkeleton />
  }

  const documents = result?.data ?? []

  return (
    <div className="space-y-1">
      {/* Create Document Button */}
      {onCreateDocument && (
        <Button
          variant="ghost"
          className="w-full justify-start gap-2 text-muted-foreground"
          onClick={() => onCreateDocument?.()}
        >
          <Plus className="h-4 w-4" />
          新建文档
        </Button>
      )}

      {/* Document Tree */}
      {documents.length > 0 ? (
        <div className="space-y-0.5">
          {documents.map((doc) => (
            <DocumentTreeItem
              key={doc.id}
              document={doc}
              knowledgeBaseId={knowledgeBaseId}
              level={0}
              isSelected={doc.id === selectedDocumentId}
              isExpanded={expandedIds.has(doc.id)}
              onToggleExpand={() => toggleExpand(doc.id)}
              onSelect={() => onSelectDocument?.(doc.id)}
              onCreateChild={onCreateDocument ? () => onCreateDocument?.(doc.id) : undefined}
              onDelete={onDeleteDocument ? () => onDeleteDocument?.(doc) : undefined}
              onMove={onMoveDocument ? () => onMoveDocument?.(doc) : undefined}
              selectedDocumentId={selectedDocumentId}
              expandedIds={expandedIds}
              onToggleExpandId={toggleExpand}
              onSelectDocument={onSelectDocument}
              onCreateDocument={onCreateDocument}
              onDeleteDocument={onDeleteDocument}
              onMoveDocument={onMoveDocument}
            />
          ))}
        </div>
      ) : (
        <div className="py-8 text-center text-sm text-muted-foreground">
          <FileText className="mx-auto mb-2 h-8 w-8 opacity-50" />
          <p>暂无文档</p>
          {onCreateDocument && (
            <p className="mt-1">点击上方按钮创建第一个文档</p>
          )}
        </div>
      )}
    </div>
  )
}

interface DocumentTreeItemProps {
  document: Document
  knowledgeBaseId: string
  level: number
  isSelected: boolean
  isExpanded: boolean
  onToggleExpand: () => void
  onSelect: () => void
  onCreateChild?: () => void
  onDelete?: () => void
  onMove?: () => void
  // For recursive rendering
  selectedDocumentId?: string
  expandedIds: Set<string>
  onToggleExpandId: (id: string) => void
  onSelectDocument?: (id: string) => void
  onCreateDocument?: (parentId?: string) => void
  onDeleteDocument?: (doc: Document) => void
  onMoveDocument?: (doc: Document) => void
}

function DocumentTreeItem({
  document,
  knowledgeBaseId,
  level,
  isSelected,
  isExpanded,
  onToggleExpand,
  onSelect,
  onCreateChild,
  onDelete,
  onMove,
  selectedDocumentId,
  expandedIds,
  onToggleExpandId,
  onSelectDocument,
  onCreateDocument,
  onDeleteDocument,
  onMoveDocument,
}: DocumentTreeItemProps) {
  const { data: childrenResult } = useDocuments(knowledgeBaseId, { parentId: document.id })
  const children = childrenResult?.data ?? []
  const hasChildren = children.length > 0

  const paddingLeft = 8 + level * 16
  const canEdit = onCreateChild || onDelete || onMove

  return (
    <div>
      <ContextMenu>
        <ContextMenuTrigger>
          <div
            className={cn(
              'group flex items-center gap-1 rounded-md py-1 pr-1 text-sm transition-colors',
              isSelected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
            )}
            style={{ paddingLeft }}
          >
            {/* Expand/Collapse Button */}
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-5 w-5 flex-shrink-0',
                !hasChildren && 'invisible'
              )}
              onClick={(e) => {
                e.stopPropagation()
                onToggleExpand()
              }}
            >
              {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </Button>

            {/* Document Link */}
            <Link
              to="/knowledge-base/documents/$documentId"
              params={{ documentId: document.id }}
              className="flex flex-1 items-center gap-2 truncate"
              onClick={onSelect}
            >
              {hasChildren ? (
                <FolderOpen className="h-4 w-4 flex-shrink-0 opacity-70" />
              ) : (
                <File className="h-4 w-4 flex-shrink-0 opacity-70" />
              )}
              <span className="truncate">{document.title}</span>
            </Link>

            {/* Actions Menu */}
            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'h-6 w-6 opacity-0 group-hover:opacity-100',
                      isSelected && 'opacity-100'
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {onCreateChild && (
                    <DropdownMenuItem onClick={onCreateChild}>
                      <Plus className="mr-2 h-4 w-4" />
                      新建子文档
                    </DropdownMenuItem>
                  )}
                  {onCreateChild && (onMove || onDelete) && <DropdownMenuSeparator />}
                  {onMove && (
                    <DropdownMenuItem onClick={onMove}>
                      <Move className="mr-2 h-4 w-4" />
                      移动
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => void navigator.clipboard.writeText(document.id)}>
                    <Copy className="mr-2 h-4 w-4" />
                    复制链接
                  </DropdownMenuItem>
                  {onDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={onDelete}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        删除
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </ContextMenuTrigger>

        {canEdit && (
          <ContextMenuContent>
            {onCreateChild && (
              <ContextMenuItem onClick={onCreateChild}>
                <Plus className="mr-2 h-4 w-4" />
                新建子文档
              </ContextMenuItem>
            )}
            {onCreateChild && (onMove || onDelete) && <ContextMenuSeparator />}
            {onMove && (
              <ContextMenuItem onClick={onMove}>
                <Move className="mr-2 h-4 w-4" />
                移动
              </ContextMenuItem>
            )}
            <ContextMenuItem onClick={() => void navigator.clipboard.writeText(document.id)}>
              <Copy className="mr-2 h-4 w-4" />
              复制链接
            </ContextMenuItem>
            {onDelete && (
              <>
                <ContextMenuSeparator />
                <ContextMenuItem
                  onClick={onDelete}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  删除
                </ContextMenuItem>
              </>
            )}
          </ContextMenuContent>
        )}
      </ContextMenu>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div>
          {children.map((child) => (
            <DocumentTreeItem
              key={child.id}
              document={child}
              knowledgeBaseId={knowledgeBaseId}
              level={level + 1}
              isSelected={child.id === selectedDocumentId}
              isExpanded={expandedIds.has(child.id)}
              onToggleExpand={() => onToggleExpandId(child.id)}
              onSelect={() => onSelectDocument?.(child.id)}
              onCreateChild={onCreateDocument ? () => onCreateDocument?.(child.id) : undefined}
              onDelete={onDeleteDocument ? () => onDeleteDocument?.(child) : undefined}
              onMove={onMoveDocument ? () => onMoveDocument?.(child) : undefined}
              selectedDocumentId={selectedDocumentId}
              expandedIds={expandedIds}
              onToggleExpandId={onToggleExpandId}
              onSelectDocument={onSelectDocument}
              onCreateDocument={onCreateDocument}
              onDeleteDocument={onDeleteDocument}
              onMoveDocument={onMoveDocument}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function DocumentTreeSkeleton() {
  return (
    <div className="space-y-1">
      <Skeleton className="h-8 w-full" />
      <div className="space-y-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-7 w-full" style={{ marginLeft: (i % 3) * 16 }} />
        ))}
      </div>
    </div>
  )
}
